import { __decorate, __awaiter } from 'tslib';
import 'long';
import '../proto/index.js';
import '../core/consts.js';
import { Logger } from '../core/metrics/logger.js';
import { Monitor, MonitorItem } from '../core/metrics/monitor.js';
import { ServiceIdentity } from '../core/service/RuntimeContext.js';
import '../core/network/NetworkItem.js';
import 'json-long';
import 'moize';
import { groupBy } from '../core/utils/tools.js';
import '../core/model/Message.js';
import 'js-md5';
import '../core/utils/lz4.js';
import '../core/utils/serializer.js';
import '../adapter/manager/AdapterManager.js';
import '../adapter/manager/types.js';
import '../core/utils/ticker.js';
import '../adapter/abstract/IMNetApi.js';
import '../core/options.js';
import '../core/utils/uuid.js';
import '../core/service/ConversationManager.js';
import '../core/service/MessageManager.js';
import '@byted/uc-secure-sdk';
import { DbProxy } from '../core/plugin/DbProxy.js';
import { TrackerItem } from '../core/metrics/trackerTypes.js';
import '../core/model/CommandMessage.js';
import { StorageMetrics, StorageMonitorItem } from './metrics/StorageMetrics.js';

class DriverProxy extends DbProxy {
    constructor(ctx, innerDriver) {
        super(ctx);
        this.installed = false;
        this.maxConversationCount = 1000;
        this.minMessageCount = 200;
        this.driver = innerDriver;
    }
    init(userId) {
        return __awaiter(this, void 0, void 0, function* () {
            this.installed = true;
            const startTime = Monitor.performanceNow();
            try {
                const isUsingDb = yield this.driver.init(userId);
                if (isUsingDb) { // if the driver reports storage is available, fill data to memory so that biz can access
                    yield this.fillData();
                    Logger.ctxDebug(this.ctx, `load data from cache for user: ${userId}`);
                }
                this.resolve(ServiceIdentity.Monitor).emitDuration(StorageMonitorItem.CacheInit + MonitorItem.SuccessSuffix, startTime);
                return isUsingDb;
            }
            catch (err) {
                Logger.ctxError(this.ctx, 'init db error:', err);
                this.resolve(ServiceIdentity.MessageManager).clearAll();
                this.resolve(ServiceIdentity.ConversationManager).clearAll();
                this.resolve(ServiceIdentity.ParticipantManager).clearAll();
                this.resolve(ServiceIdentity.Monitor).emitDuration(StorageMonitorItem.CacheInit + MonitorItem.ErrorSuffix, startTime);
                return false;
            }
        });
    }
    clear() {
        return this.driver.clear();
    }
    loadMessage() {
        return this.driver.loadMessage();
    }
    loadConversation() {
        return this.driver.loadConversation();
    }
    loadParticipants() {
        return this.driver.loadParticipants();
    }
    upsertMessage(m) {
        var _a;
        return __awaiter(this, void 0, void 0, function* () {
            try {
                yield this.driver.upsertMessage(m);
            }
            catch (err) {
                if (err !== undefined) {
                    this.resolve(ServiceIdentity.Monitor).emitTracker(TrackerItem.SaveMessageError, {
                        error_msg: err.message,
                        error_stack: err.stack,
                        msg_source: Number.parseInt((_a = m.source.toString()) !== null && _a !== void 0 ? _a : '-1', 10)
                    });
                }
                throw err;
            }
        });
    }
    upsertConversation(c) {
        return this.driver.upsertConversation(c);
    }
    upsertParticipant(p) {
        return this.driver.upsertParticipant(p);
    }
    deleteMessage(m) {
        return this.driver.deleteMessage(m);
    }
    deleteConversation(c) {
        return this.driver.deleteConversation(c);
    }
    deleteParticipant(p) {
        return this.driver.deleteParticipant(p);
    }
    clearConversation(c) {
        return this.driver.clearConversation(c);
    }
    loadUserCursor(inboxType) {
        return __awaiter(this, void 0, void 0, function* () {
            return this.driver.loadUserCursor(inboxType);
        });
    }
    saveUserCursor(inboxType, cursor) {
        return this.driver.saveUserCursor(inboxType, cursor);
    }
    saveConfig(key, value) {
        return this.driver.saveConfig(key, value);
    }
    loadConfig(key) {
        return this.driver.loadConfig(key);
    }
    isOlderThan(date, days) {
        return date.getTime() + days * 86400 * 1000 < Date.now();
    }
    fillData() {
        return __awaiter(this, void 0, void 0, function* () {
            const convAll = yield this.driver.loadConversation();
            const conv = convAll.filter(c => this.resolve(ServiceIdentity.InboxType).isValidInbox(c.inboxType));
            this.resolve(ServiceIdentity.ConversationManager).applyLocal(conv);
            if (convAll.length !== conv.length && convAll.length !== 0) {
                Logger.ctxWarn(this.ctx, `skip ${convAll.length - conv.length} conversation for invalid inbox`);
            }
            const msgAll = yield this.driver.loadMessage();
            const msg = msgAll.filter(m => conv.some(c => c.id === m.conversationId));
            this.resolve(ServiceIdentity.MessageManager).applyLocal(msg);
            if (msgAll.length !== msg.length && msgAll.length !== 0) {
                Logger.ctxWarn(this.ctx, `skip ${msgAll.length - msg.length} message for invalid inbox`);
            }
            if (this.resolve(ServiceIdentity.ParticipantManager)) {
                const parAll = yield this.driver.loadParticipants();
                const par = parAll.filter(p => conv.some(c => c.id === p.conversationId));
                if (parAll.length !== par.length && parAll.length !== 0) {
                    Logger.ctxWarn(this.ctx, `skip ${parAll.length - par.length} participant for invalid inbox`);
                }
                this.resolve(ServiceIdentity.ParticipantManager).applyLocal(par);
            }
            this.pruneData();
            let retentionTime = -1;
            if (typeof this.ctx.option.retention === 'number') {
                retentionTime = this.ctx.option.retention;
            }
            else if (this.ctx.option.retention === true) {
                retentionTime = 0;
            }
            if (retentionTime >= 0) {
                this.retention(retentionTime, msg);
            }
        });
    }
    pruneData() {
        const startTime = Monitor.performanceNow();
        // clear messages that conversation not exist in local already
        // applied next time
        const allConversations = this.resolve(ServiceIdentity.ConversationManager).getConversationArray(() => true);
        const allMessages = this.resolve(ServiceIdentity.MessageManager).getRawMap();
        const hashMap = allConversations.map(c => c.id).reduce((acc, current) => {
            acc[current] = true;
            return acc;
        }, {});
        const toPrune = [];
        allMessages.forEach((_, conversationId) => {
            if (hashMap[conversationId] === undefined) {
                toPrune.push(conversationId);
            }
        });
        if (toPrune.length > 0) {
            Logger.ctxWarn(this.ctx, 'prune conversations:', toPrune);
            setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                for (const c of toPrune) {
                    yield this.deleteConversation({ id: c });
                }
            }), 0);
        }
        this.resolve(ServiceIdentity.Monitor).emitDuration(StorageMonitorItem.CachePrune, startTime);
    }
    retention(retentionDays, msg) {
        const startTime = Monitor.performanceNow();
        // we need a sorted conversation
        // delete conversations more than "maxConversationCount"
        const convs = this.resolve(ServiceIdentity.ConversationManager).getConversationArray(() => true);
        if (convs.length > this.maxConversationCount) {
            const toDeleteConv = [];
            while (convs.length > this.maxConversationCount) {
                toDeleteConv.push(convs.pop());
            }
            if (toDeleteConv.length > 0) {
                setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                    for (const c of toDeleteConv) {
                        yield this.deleteConversation(c);
                    }
                }));
                Logger.ctxWarn(this.ctx, `retention ${toDeleteConv.length} overflow conversations`);
            }
        }
        // delete expired message
        const toDeleteMsg = [];
        if (retentionDays >= 0 && msg !== undefined && msg.length > 0) {
            // group messages by conversation
            const convMap = groupBy(msg, m => m.conversationId);
            for (const key of Object.keys(convMap)) {
                const currentConversation = convMap[key];
                // save at least "minMessageCount" messages for each conversation...
                if (currentConversation.length <= this.minMessageCount) {
                    continue;
                }
                // while we need to delete messages...
                // we assume that messages are sorted in time order
                // so we just scan from top and mark every messages
                // until the count is less than "minMessageCount" or x(retention) days
                let restMessageCount = currentConversation.length;
                for (const m of currentConversation) {
                    if (restMessageCount <= this.minMessageCount) {
                        break;
                    }
                    if (this.isOlderThan(m.createdAt, retentionDays)) {
                        toDeleteMsg.push(m);
                        restMessageCount--;
                    }
                }
            }
            if (toDeleteMsg.length > 0) {
                setTimeout(() => __awaiter(this, void 0, void 0, function* () {
                    for (const m of toDeleteMsg) {
                        yield this.deleteMessage(m);
                    }
                }), 0);
                Logger.ctxWarn(this.ctx, `retention ${toDeleteMsg.length} overflow messages`);
            }
        }
        this.resolve(ServiceIdentity.Monitor).emitDuration(StorageMonitorItem.CacheRetention, startTime);
    }
}
__decorate([
    StorageMetrics
], DriverProxy.prototype, "clear", null);
__decorate([
    StorageMetrics
], DriverProxy.prototype, "loadMessage", null);
__decorate([
    StorageMetrics
], DriverProxy.prototype, "loadConversation", null);
__decorate([
    StorageMetrics
], DriverProxy.prototype, "loadParticipants", null);
__decorate([
    StorageMetrics
], DriverProxy.prototype, "upsertMessage", null);
__decorate([
    StorageMetrics
], DriverProxy.prototype, "upsertConversation", null);
__decorate([
    StorageMetrics
], DriverProxy.prototype, "upsertParticipant", null);
__decorate([
    StorageMetrics
], DriverProxy.prototype, "deleteMessage", null);
__decorate([
    StorageMetrics
], DriverProxy.prototype, "deleteConversation", null);
__decorate([
    StorageMetrics
], DriverProxy.prototype, "deleteParticipant", null);
__decorate([
    StorageMetrics
], DriverProxy.prototype, "clearConversation", null);
__decorate([
    StorageMetrics
], DriverProxy.prototype, "loadUserCursor", null);
__decorate([
    StorageMetrics
], DriverProxy.prototype, "saveUserCursor", null);
__decorate([
    StorageMetrics
], DriverProxy.prototype, "saveConfig", null);
__decorate([
    StorageMetrics
], DriverProxy.prototype, "loadConfig", null);

export { DriverProxy };
//# sourceMappingURL=DriverProxy.js.map
